import { RouteType } from "types/RouteType";
import type RootStore from "stores/RootStore";
import type EvereeWorkerManagementStore from "./workers/EvereeWorkerManagement.Store";
import type EvereeTimesheetManagementStore from "./timesheets/EvereeTimesheetManagement.Store";
import type EvereePayrollOperationsStore from "./operations/EvereePayrollOperations.Store";
import type EvereeWorkLocationsStore from "./locations/EvereeWorkLocations.Store";
import type EvereeFinancialReportsStore from "./reports/EvereeFinancialReports.Store";

/**
 * Everee Admin - Worker Management Route
 * Manage workers, employees, candidates, and documents
 */
export const evereeWorkerManagementRoute: RouteType = {
  name: "Worker Management",
  path: "/payroll/admin/workers",
  layout: "/app",
  defaultQueryParams: { companyId: "H2" },
  viewportCard: {
    header: {
      title: "Worker Management",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./workers/EvereeWorkerManagement.Header"),
    },
    body: {
      component: () => import("./workers/EvereeWorkerManagement.Body"),
      grayBackground: true,
    },
  },
  dialogs: [
    {
      name: "CreateWorkerDialog",
      title: "Create Worker",
      component: () => import("./workers/CreateWorkerDialog"),
    },
    {
      name: "WorkerDetailDialog",
      title: "Worker Details",
      component: () => import("./workers/WorkerDetailDialog"),
      maxWidth: "md",
      height: 500,
    },
  ],
  group: "Payroll Admin",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeWorkerManagementStore as EvereeWorkerManagementStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadWorkers(companyId);
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeWorkerManagementStore as EvereeWorkerManagementStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadWorkers(companyId);
    }
  },
};

/**
 * Everee Admin - Timesheet Management Route
 * Manage timesheets, approve/reject shifts
 */
export const evereeTimesheetManagementRoute: RouteType = {
  name: "Timesheet Management",
  path: "/payroll/admin/timesheets",
  layout: "/app",
  defaultQueryParams: { companyId: "H2" },
  viewportCard: {
    header: {
      title: "Timesheet Management",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./timesheets/EvereeTimesheetManagement.Header"),
    },
    body: {
      component: () => import("./timesheets/EvereeTimesheetManagement.Body"),
      grayBackground: true,
    },
  },
  dialogs: [
    {
      name: "TimesheetEntryDialog",
      title: "Timesheet Entry",
      component: () => import("./timesheets/TimesheetEntryDialog"),
    },
  ],
  group: "Payroll Admin",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeTimesheetManagementStore as EvereeTimesheetManagementStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadTimesheets(companyId);
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeTimesheetManagementStore as EvereeTimesheetManagementStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadTimesheets(companyId);
    }
  },
};

/**
 * Everee Admin - Payroll Operations Route
 * Manage payables, early pay, and realtime payroll settings
 */
export const evereePayrollOperationsRoute: RouteType = {
  name: "Payroll Operations",
  path: "/payroll/admin/operations",
  layout: "/app",
  defaultQueryParams: { companyId: "H2" },
  viewportCard: {
    header: {
      title: "Payroll Operations",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./operations/EvereePayrollOperations.Header"),
    },
    body: {
      component: () => import("./operations/EvereePayrollOperations.Body"),
      grayBackground: true,
    },
  },
  dialogs: [
    {
      name: "CreatePayableDialog",
      title: "Create Payable",
      component: () => import("./operations/CreatePayableDialog"),
    },
  ],
  group: "Payroll Admin",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereePayrollOperationsStore as EvereePayrollOperationsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadPayables(companyId);
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereePayrollOperationsStore as EvereePayrollOperationsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadPayables(companyId);
    }
  },
};

/**
 * Everee Admin - Work Locations Route
 * Manage physical work locations
 */
export const evereeWorkLocationsRoute: RouteType = {
  name: "Work Locations",
  path: "/payroll/admin/locations",
  layout: "/app",
  defaultQueryParams: { companyId: "H2" },
  viewportCard: {
    header: {
      title: "Work Locations",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./locations/EvereeWorkLocations.Header"),
    },
    body: {
      component: () => import("./locations/EvereeWorkLocations.Body"),
      grayBackground: true,
    },
  },
  dialogs: [
    {
      name: "LocationDialog",
      title: "Work Location",
      component: () => import("./locations/LocationDialog"),
    },
  ],
  group: "Payroll Admin",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeWorkLocationsStore as EvereeWorkLocationsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadLocations(companyId);
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeWorkLocationsStore as EvereeWorkLocationsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadLocations(companyId);
    }
  },
};

/**
 * Everee Admin - Financial Reports Route
 * View payment history, gross earnings, and expenses
 */
export const evereeFinancialReportsRoute: RouteType = {
  name: "Financial Reports",
  path: "/payroll/admin/reports",
  layout: "/app",
  defaultQueryParams: { companyId: "H2" },
  viewportCard: {
    header: {
      title: "Financial Reports",
      enableRefreshButton: true,
      enableBackButton: true,
      color: "primary",
      component: () => import("./reports/EvereeFinancialReports.Header"),
    },
    body: {
      component: () => import("./reports/EvereeFinancialReports.Body"),
      grayBackground: true,
    },
  },
  group: "Payroll Admin",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  enableAutoFind: false,
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeFinancialReportsStore as EvereeFinancialReportsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadReports(companyId);
    }
    return true;
  },
  onParamsChange: async ({ rootStore }: { rootStore: RootStore }) => {
    const store =
      rootStore.evereeFinancialReportsStore as EvereeFinancialReportsStore;
    const companyId = rootStore.routerStore?.queryParams?.companyId as string;
    if (store) {
      await store.loadReports(companyId);
    }
  },
};

export default [
  evereeWorkerManagementRoute,
  evereeTimesheetManagementRoute,
  evereePayrollOperationsRoute,
  evereeWorkLocationsRoute,
  evereeFinancialReportsRoute,
];
